import { tileToScreen } from '../sim/projection';
import { CLIMB } from '../sim/map';

/**
 * Sloped mesh rendering (true terrain height, PRD #536 Slice E) - the PURE
 * geometry half, no Pixi import, so corner interpolation / vertex math / the
 * cliff-face predicate stay node-testable. `terrainMesh.ts` is the thin Pixi
 * glue over these builders.
 *
 * ## The corner convention
 *
 * The per-cell `heights` value is read as a **corner heightmap** (the PRD's
 * format decision - "one z per cell, which IS a corner heightmap when read as
 * this cell's reference corner"): `heights[y*w+x]` is the z of the lattice
 * corner at tile coordinates (x, y), which is the tile's **north (screen-top)
 * corner** - the exact point `tileToScreen(x, y)` projects, and the point the
 * terraced path (Slice C) hangs the whole flat diamond from. A tile's other
 * three screen corners are the neighbouring cells' reference corners:
 *
 *   east  (screen-right)  = cell (x+1, y)
 *   south (screen-bottom) = cell (x+1, y+1)
 *   west  (screen-left)   = cell (x,   y+1)
 *
 * Because adjacent tiles share lattice corners, the mesh surface is
 * **continuous by construction** - no seams, and no open cliff gap (ADR-0267's
 * deferred artifact): a > CLIMB edge renders as a steeply stretched tile whose
 * quad IS the cliff face. At the map's east/south fringe the missing neighbour
 * cell **clamps to the last cell** (`min(cx, width-1)`), so the border row
 * stays level at its own z instead of ramping down to 0.
 */

/** A map-ish source of heights + art names; `GameMap` satisfies it. */
export interface HeightedMapLike {
  width: number;
  height: number;
  isPlain?: boolean;
  hasHeights?: boolean;
  landZ(x: number, y: number): number;
  tileNameAt(x: number, y: number): string | undefined;
}

/** The z of lattice corner (cx, cy): the clamped cell's reference corner. */
export function cornerZ(map: HeightedMapLike, cx: number, cy: number): number {
  return map.landZ(Math.min(cx, map.width - 1), Math.min(cy, map.height - 1));
}

/** A tile's four corner elevations, in screen-corner terms (n = top, ...). */
export interface TileCorners {
  n: number;
  e: number;
  s: number;
  w: number;
}

/** Derive tile (x, y)'s four corner z values from the corner heightmap. */
export function tileCorners(map: HeightedMapLike, x: number, y: number): TileCorners {
  return {
    n: cornerZ(map, x, y),
    e: cornerZ(map, x + 1, y),
    s: cornerZ(map, x + 1, y + 1),
    w: cornerZ(map, x, y + 1),
  };
}

/**
 * The deformed diamond: each corner projected at its own z through the one
 * shared projection. 8 floats, N/E/S/W order. With all-zero corners this is
 * pixel-exact the diamond the Slice C sprite paints (anchor (0.5, 0) at
 * `tileToScreen(x, y)`), which is the flat-tile identity the tests pin.
 */
export function tileMeshPositions(x: number, y: number, c: TileCorners): number[] {
  const n = tileToScreen(x, y, c.n);
  const e = tileToScreen(x + 1, y, c.e);
  const s = tileToScreen(x + 1, y + 1, c.s);
  const w = tileToScreen(x, y + 1, c.w);
  return [n.x, n.y, e.x, e.y, s.x, s.y, w.x, w.y];
}

/** A baked land tile's frame rectangle inside the land atlas (pixels). */
export interface AtlasFrame {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * UVs for the four diamond corners: the same baked 44x44 land art the sprite
 * path draws, sampled at the frame's diamond midpoints (top-centre,
 * right-middle, bottom-centre, left-middle) and stretched over the deformed
 * quad - shard-style, no texmap pipeline. Normalized against the whole atlas
 * because the mesh textures the atlas source directly.
 */
export function tileMeshUvs(frame: AtlasFrame, atlasW: number, atlasH: number): number[] {
  const cx = frame.x + frame.w / 2;
  const cy = frame.y + frame.h / 2;
  return [
    cx / atlasW,
    frame.y / atlasH,
    (frame.x + frame.w) / atlasW,
    cy / atlasH,
    cx / atlasW,
    (frame.y + frame.h) / atlasH,
    frame.x / atlasW,
    cy / atlasH,
  ];
}

/** Two triangles per tile quad: (N,E,S) and (N,S,W), both screen-CW. */
export const TILE_MESH_INDICES: readonly number[] = [0, 1, 2, 0, 2, 3];

/**
 * Whether a tile's quad is a **cliff face**: its corner spread exceeds the
 * climb rule, so no mobile can walk across it and it reads as a vertical wall
 * (or pit rim - |dz| symmetric, like `GameMap.canStep`). These tiles get the
 * darkening tint so the stretched land art (or a flat-colour stub) reads as a
 * face rather than more floor.
 */
export function isCliffFace(c: TileCorners): boolean {
  const max = Math.max(c.n, c.e, c.s, c.w);
  const min = Math.min(c.n, c.e, c.s, c.w);
  return max - min > CLIMB;
}

/**
 * Chunk edge in tiles. 16x16 = 256 quads / 1024 vertices per mesh: big enough
 * that a 128x128 map is 64 draw-call-friendly meshes instead of 16k sprites,
 * small enough that chunk-granular viewport culling still discards most
 * off-screen ground.
 */
export const TERRAIN_CHUNK_TILES = 16;

/** One chunk's ready-to-upload buffers plus the metadata the renderer needs. */
export interface TerrainMeshChunkData {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  positions: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
  /** Every tile the chunk drew, with its cell landZ (budget/pick lookup). */
  tiles: { x: number; y: number; z: number }[];
  /** The deformed 8-float diamond of each > CLIMB tile (the face tint polys). */
  cliffFaces: number[][];
}

/**
 * Build the whole map's chunked mesh buffers. Tiles whose art is missing are
 * skipped exactly like `createTerrain` skips them (the stub-degrade contract);
 * a chunk with no drawable tile is dropped entirely.
 *
 * Paint-order caveat: tiles draw row-major WITHIN a chunk (back-to-front, the
 * `createTerrain` contract), but chunks themselves add chunk-row-major, so a
 * steep quad whose screen extent crosses a 16-tile chunk seam can be
 * overpainted by the later-added east/south chunk. This is a known artifact in
 * the same deferred family as land-vs-mobile sorting (ADR-0272): visible only
 * when a > CLIMB face straddles a seam, and properly solved by sorting land,
 * not by re-ordering chunks.
 */
export function buildTerrainMeshChunks(
  map: HeightedMapLike,
  frameFor: (tileName: string) => AtlasFrame | undefined,
  atlasW: number,
  atlasH: number,
  chunkTiles: number = TERRAIN_CHUNK_TILES,
): TerrainMeshChunkData[] {
  const chunks: TerrainMeshChunkData[] = [];
  for (let cy = 0; cy < map.height; cy += chunkTiles) {
    for (let cx = 0; cx < map.width; cx += chunkTiles) {
      const maxX = Math.min(cx + chunkTiles - 1, map.width - 1);
      const maxY = Math.min(cy + chunkTiles - 1, map.height - 1);
      const positions: number[] = [];
      const uvs: number[] = [];
      const indices: number[] = [];
      const tiles: TerrainMeshChunkData['tiles'] = [];
      const cliffFaces: number[][] = [];
      // Row-major, like createTerrain: back-to-front paint order within the
      // chunk (matters only where steep quads overlap their front neighbours).
      for (let y = cy; y <= maxY; y++) {
        for (let x = cx; x <= maxX; x++) {
          const name = map.tileNameAt(x, y);
          const frame = name !== undefined ? frameFor(name) : undefined;
          if (!frame) continue; // same degrade as createTerrain: skip, warned by the glue
          const corners = tileCorners(map, x, y);
          const quad = tileMeshPositions(x, y, corners);
          const base = positions.length / 2;
          positions.push(...quad);
          uvs.push(...tileMeshUvs(frame, atlasW, atlasH));
          for (const i of TILE_MESH_INDICES) indices.push(base + i);
          tiles.push({ x, y, z: map.landZ(x, y) });
          if (isCliffFace(corners)) cliffFaces.push(quad);
        }
      }
      if (tiles.length === 0) continue;
      chunks.push({
        minX: cx,
        minY: cy,
        maxX,
        maxY,
        positions: new Float32Array(positions),
        uvs: new Float32Array(uvs),
        indices: new Uint32Array(indices),
        tiles,
        cliffFaces,
      });
    }
  }
  return chunks;
}

/**
 * The render-path toggle (opt-in until the mesh path is captain-eyeballed on
 * real art): `?terrain=mesh` on the URL (the `?dev=` convention) for a one-off
 * session, or `localStorage['britbank.terrainMesh'] = '1'` to persist. The URL
 * param wins in BOTH directions - `?terrain=sprite` forces the terraced sprite
 * path for one session even when the persistent flag is set - so a captain can
 * A/B the two paths without touching localStorage. Default = terraced
 * (Slice C). Graduation path: flip the default here once eyeballed, keeping
 * the sprite path as the flat-map fast path.
 */
export function terrainMeshEnabled(search: string, stored: string | null): boolean {
  const param = new URLSearchParams(search).get('terrain');
  if (param === 'mesh') return true;
  if (param === 'sprite') return false;
  return stored === '1';
}

/** The localStorage key backing the persistent half of the toggle. */
export const TERRAIN_MESH_STORAGE_KEY = 'britbank.terrainMesh';

/**
 * Whether the scene should build the mesh ground instead of sprites: only when
 * toggled on AND the map actually carries heights. A flat map (wbb.json) keeps
 * the sprite path even with the toggle on - a flat mesh would land pixel-exact
 * where the sprites do (pinned by the flat-tile identity test), so sprites ARE
 * the identical output, with zero risk and the warmer code path. Open plains
 * are gridless (heights refused by contract) and always procedural.
 */
export function useMeshTerrainPath(
  enabled: boolean,
  map: Pick<HeightedMapLike, 'isPlain' | 'hasHeights'>,
): boolean {
  return enabled && !map.isPlain && map.hasHeights === true;
}
